import { OnboardingLanguage } from "./onboarding.enums";

/**
 * The supported-country catalog + country->language map for onboarding smart defaults (ADR-0020). App-agnostic
 * and the single shared source: the Platform Credits billing catalog converges on this when that branch merges,
 * and a church's country here seeds its eventual billing market (country -> currency -> operators). These are
 * editable pre-selection defaults, never a hard gate. DR Congo is the reference market.
 */
export interface OnboardingCountry {
  /** ISO 3166-1 alpha-3 (e.g. `COD`). */
  code: string;
  /** English display name; localized in the UI. */
  name: string;
  /** ISO 3166-1 alpha-2 (e.g. `CD`) — for flag/locale hints and the phone input. */
  iso2: string;
  /** The default UI language for churches in this country. */
  defaultLanguage: OnboardingLanguage;
}

export const ONBOARDING_COUNTRIES: readonly OnboardingCountry[] = [
  { code: "COD", name: "DR Congo", iso2: "CD", defaultLanguage: OnboardingLanguage.French },
  { code: "RWA", name: "Rwanda", iso2: "RW", defaultLanguage: OnboardingLanguage.Kinyarwanda },
  { code: "BDI", name: "Burundi", iso2: "BI", defaultLanguage: OnboardingLanguage.French },
  { code: "KEN", name: "Kenya", iso2: "KE", defaultLanguage: OnboardingLanguage.English },
  { code: "UGA", name: "Uganda", iso2: "UG", defaultLanguage: OnboardingLanguage.English },
  { code: "TZA", name: "Tanzania", iso2: "TZ", defaultLanguage: OnboardingLanguage.English },
  { code: "CMR", name: "Cameroon", iso2: "CM", defaultLanguage: OnboardingLanguage.French },
];

/** Reference-market fallbacks used when no signal resolves a value. */
export const DEFAULT_ONBOARDING_COUNTRY = "COD";
export const DEFAULT_ONBOARDING_LANGUAGE = OnboardingLanguage.French;

/** Look up a supported country by ISO-3 code (case-insensitive), or undefined. */
export function onboardingCountry(code?: string | null): OnboardingCountry | undefined {
  if (!code) return undefined;
  const upper = code.toUpperCase();
  return ONBOARDING_COUNTRIES.find((entry) => entry.code === upper);
}

/** Look up a supported country by ISO 3166-1 alpha-2 (case-insensitive) — e.g. from a CDN geo header. */
export function onboardingCountryFromIso2(iso2?: string | null): OnboardingCountry | undefined {
  if (!iso2) return undefined;
  const upper = iso2.toUpperCase();
  return ONBOARDING_COUNTRIES.find((entry) => entry.iso2 === upper);
}

/** The default language for a country, or the global fallback. */
export function countryDefaultLanguage(code?: string | null): OnboardingLanguage {
  return onboardingCountry(code)?.defaultLanguage ?? DEFAULT_ONBOARDING_LANGUAGE;
}

/** Map an IANA timezone to a supported ISO-3 country (a hint only), or undefined. */
const TIMEZONE_COUNTRY: Record<string, string> = {
  "Africa/Kinshasa": "COD",
  "Africa/Lubumbashi": "COD",
  "Africa/Kigali": "RWA",
  "Africa/Bujumbura": "BDI",
  "Africa/Nairobi": "KEN",
  "Africa/Kampala": "UGA",
  "Africa/Dar_es_Salaam": "TZA",
  "Africa/Douala": "CMR",
};

export function countryFromTimezone(timezone?: string | null): string | undefined {
  if (!timezone) return undefined;
  return TIMEZONE_COUNTRY[timezone];
}

/** Primary IANA timezone per supported country — seeds a claimed workspace's (and its branches') timezone. */
const COUNTRY_PRIMARY_TIMEZONE: Record<string, string> = {
  COD: "Africa/Kinshasa",
  RWA: "Africa/Kigali",
  BDI: "Africa/Bujumbura",
  KEN: "Africa/Nairobi",
  UGA: "Africa/Kampala",
  TZA: "Africa/Dar_es_Salaam",
  CMR: "Africa/Douala",
};

/** The default timezone for a supported country, or the reference-market fallback (DR Congo / Kinshasa). */
export function countryPrimaryTimezone(code?: string | null): string {
  return COUNTRY_PRIMARY_TIMEZONE[(code ?? "").toUpperCase()] ?? COUNTRY_PRIMARY_TIMEZONE[DEFAULT_ONBOARDING_COUNTRY];
}

/** Normalize a browser / Accept-Language tag (e.g. `fr-FR`) to a supported {@link OnboardingLanguage}, or undefined. */
export function normalizeLanguage(tag?: string | null): OnboardingLanguage | undefined {
  if (!tag) return undefined;
  const base = tag.toLowerCase().split("-")[0];
  const supported = Object.values(OnboardingLanguage) as string[];
  return supported.includes(base) ? (base as OnboardingLanguage) : undefined;
}
